import { FC, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { ITrialLesson, trialLessonService, TTrialLessonAction, TTrialLessonLostType } from "../../../entities/trialLesson";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { Modal } from "../../../shared/ui/modal";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import classes from './trialLessonActions.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    trialLesson: ITrialLesson;
    action: TTrialLessonAction;
    onSuccess: () => void;
}

const ACTION_TITLES: Partial<Record<TTrialLessonAction, string>> = {
    TRIAL_LESSON_ACTION_COMPLETE: 'Провести пробное',
    TRIAL_LESSON_ACTION_MARK_LOST: 'Слив (отметить)',
    TRIAL_LESSON_ACTION_REOPEN_LOST: 'Откатить слив',
    TRIAL_LESSON_ACTION_DELETE: 'Удалить пробное',
}

const CONFIRM_TITLES: Partial<Record<TTrialLessonAction, string>> = {
    TRIAL_LESSON_ACTION_COMPLETE: 'Точно отметить пробное проведённым?',
    TRIAL_LESSON_ACTION_MARK_LOST: 'Точно отметить пробное как слив?',
    TRIAL_LESSON_ACTION_REOPEN_LOST: 'Точно откатить слив?',
    TRIAL_LESSON_ACTION_DELETE: 'Точно удалить пробное?',
}

const SUCCESS_MESSAGES: Partial<Record<TTrialLessonAction, string>> = {
    TRIAL_LESSON_ACTION_COMPLETE: 'Пробное отмечено проведённым',
    TRIAL_LESSON_ACTION_MARK_LOST: 'Пробное отмечено как слив',
    TRIAL_LESSON_ACTION_REOPEN_LOST: 'Слив пробного откатан',
    TRIAL_LESSON_ACTION_DELETE: 'Пробное удалено',
}

const LOST_TYPE_OPTIONS: Array<{value: TTrialLessonLostType; label: string}> = [
    {value: 'TRIAL_LESSON_LOST_TYPE_BEFORE_TRIAL', label: 'До пробного'},
    {value: 'TRIAL_LESSON_LOST_TYPE_AFTER_TRIAL', label: 'После пробного'},
]

export const TrialLessonActions: FC<IProps> = ({open, setOpen, trialLesson, action, onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [confirm, setConfirm] = useState<boolean>(action === 'TRIAL_LESSON_ACTION_COMPLETE')
    const [comment, setComment] = useState<string>('')
    const [lostType, setLostType] = useState<TTrialLessonLostType>('TRIAL_LESSON_LOST_TYPE_UNSPECIFIED')
    const [error, setError] = useState<string>('')

    useEffect(() => {
        if(!open) return

        setConfirm(action === 'TRIAL_LESSON_ACTION_COMPLETE')
        setComment('')
        setLostType('TRIAL_LESSON_LOST_TYPE_UNSPECIFIED')
        setError('')
    }, [action, open])

    const onClose = (value: boolean) => {
        if(!value){
            setConfirm(false)
            setError('')
        }
        setOpen(value)
    }

    const prepareConfirm = () => {
        if(!comment.trim()){
            setError(action === 'TRIAL_LESSON_ACTION_MARK_LOST'
                ? 'Укажите причину слива'
                : action === 'TRIAL_LESSON_ACTION_REOPEN_LOST'
                    ? 'Укажите причину отката'
                : 'Укажите причину удаления'
            )
            return
        }
        if(action === 'TRIAL_LESSON_ACTION_MARK_LOST' && lostType === 'TRIAL_LESSON_LOST_TYPE_UNSPECIFIED'){
            setError('Выберите тип слива')
            return
        }

        setError('')
        setConfirm(true)
    }

    const submit = async () => {
        try{
            setIsLoading(true)

            if(action === 'TRIAL_LESSON_ACTION_COMPLETE'){
                await trialLessonService.completeTrialLesson(trialLesson.id)
            }
            if(action === 'TRIAL_LESSON_ACTION_MARK_LOST'){
                await trialLessonService.markTrialLessonLost({
                    trialLessonId: trialLesson.id,
                    comment: comment.trim(),
                    lostType,
                })
            }
            if(action === 'TRIAL_LESSON_ACTION_REOPEN_LOST'){
                await trialLessonService.reopenLostTrialLesson({
                    trialLessonId: trialLesson.id,
                    comment: comment.trim(),
                })
            }
            if(action === 'TRIAL_LESSON_ACTION_DELETE'){
                await trialLessonService.deleteTrialLesson({
                    trialLessonId: trialLesson.id,
                    comment: comment.trim(),
                })
            }

            setGlobalMessage({message: SUCCESS_MESSAGES[action] ?? 'Действие выполнено', type: 'ok'})
            onClose(false)
            onSuccess()
        }
        catch(requestError){
            console.log(requestError)

            if(requestError instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: requestError.message, type: 'error'})
            }
            else if(requestError instanceof MyError){
                setGlobalMessage({message: requestError.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при выполнении действия с пробным занятием', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const needsComment = action === 'TRIAL_LESSON_ACTION_MARK_LOST' ||
        action === 'TRIAL_LESSON_ACTION_REOPEN_LOST' ||
        action === 'TRIAL_LESSON_ACTION_DELETE'

    return (
        <Modal open={open} setOpen={onClose}>
            {confirm ? (
                <ConfirmationAction
                    title={CONFIRM_TITLES[action] ?? 'Точно выполнить действие?'}
                    setOpen={value => {
                        if(action === 'TRIAL_LESSON_ACTION_COMPLETE') onClose(value)
                        else setConfirm(value)
                    }}
                    onClick={submit}
                    type={action === 'TRIAL_LESSON_ACTION_COMPLETE' || action === 'TRIAL_LESSON_ACTION_REOPEN_LOST' ? 'send' : 'delete'}
                    isLoading={isLoading}
                />
            ) : (
                <section className={classes.container}>
                    <section className={classes.header}>
                        <h3>{ACTION_TITLES[action] ?? 'Действие с пробным'}</h3>
                        <button type="button" onClick={() => onClose(false)} className={classes.closeButton}>×</button>
                    </section>

                    {needsComment && (
                        <>
                            {action === 'TRIAL_LESSON_ACTION_MARK_LOST' && (
                                <section className={classes.lostTypeGroup}>
                                    <span className={classes.fieldTitle}>Тип слива</span>
                                    <section className={classes.lostTypeOptions}>
                                        {LOST_TYPE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={lostType === option.value ? classes.selectedLostType : ''}
                                                onClick={() => {
                                                    setLostType(option.value)
                                                    setError('')
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </section>
                                </section>
                            )}
                            <MyTextarea
                                title={action === 'TRIAL_LESSON_ACTION_MARK_LOST'
                                    ? 'Причина слива'
                                    : action === 'TRIAL_LESSON_ACTION_REOPEN_LOST'
                                        ? 'Причина отката'
                                        : 'Причина удаления'}
                                value={comment}
                                setValue={setComment}
                                setError={setError}
                                placeholder="Например: ученик не вышел на связь"
                            />
                        </>
                    )}

                    <section className={classes.footer}>
                        <section className={classes.secondaryButton}>
                            <MyButtonSecondary onClick={() => onClose(false)}>Отмена</MyButtonSecondary>
                        </section>
                        <section className={classes.primaryButton}>
                            <MyButton error={error} onClick={prepareConfirm}>Продолжить</MyButton>
                        </section>
                    </section>
                </section>
            )}
        </Modal>
    )
}
